<section class="d-flex flex-column gap-2" data-testid="licence-portfolio-page">
  <div class="d-flex flex-wrap align-items-center justify-content-between gap-2">
    <div class="d-flex flex-column gap-1">
      <h2 class="av-section-title m-0">{{ 'backoffice.licences.portfolio.title' | translate }}</h2>
      <p class="av-meta m-0">{{ 'backoffice.licences.portfolio.subtitle' | translate }}</p>
    </div>

    <button type="button" class="av-btn" (click)="reload()" data-testid="portfolio-reload">
      <mat-icon aria-hidden="true">refresh</mat-icon>
      <span>{{ 'backoffice.common.reload' | translate }}</span>
    </button>
  </div>

  <p class="av-meta m-0" data-testid="portfolio-scope-note">{{ 'backoffice.licences.portfolio.scope_note' | translate }}</p>

  <div class="d-flex flex-wrap align-items-end gap-2">
    <av-organization-picker
      [organizations]="facade.organizations()"
      [value]="facade.organizationId()"
      (valueChange)="selectOrganization($event)"
    ></av-organization-picker>
  </div>

  @if (!facade.canAdjust()) {
    <p class="av-meta m-0" data-testid="portfolio-restricted">{{ 'backoffice.licences.notes.restricted' | translate }}</p>
  }
</section>

@if (!facade.hasOrganization()) {
  <av-empty-state
    class="mt-3"
    icon="corporate_fare"
    [title]="'backoffice.licences.portfolio.pick_title' | translate"
    [message]="'backoffice.licences.portfolio.pick_message' | translate"
  ></av-empty-state>
} @else {
  @switch (facade.state()) {
    @case ('error') {
      <av-empty-state
        class="mt-3"
        icon="error_outline"
        [title]="'backoffice.licences.error.title' | translate"
        [message]="'backoffice.licences.error.message' | translate"
      ></av-empty-state>
    }
    @case ('ready') {
      @if (!facade.hasSubscription()) {
        <av-empty-state
          class="mt-3"
          icon="receipt_long"
          [title]="'backoffice.licences.empty.no_subscription_title' | translate"
          [message]="'backoffice.licences.empty.no_subscription_message' | translate"
        ></av-empty-state>
      } @else {
        <section class="av-card mt-3 p-4 d-flex flex-column gap-3" data-testid="portfolio-components">
          <div class="d-flex flex-column gap-1">
            <h3 class="av-section-title m-0">{{ 'backoffice.licences.portfolio.components_title' | translate }}</h3>
            <p class="av-meta m-0">{{ 'backoffice.licences.hint' | translate }}</p>
          </div>

          <div class="table-scroll">
            <table class="av-table w-100" data-testid="component-table">
              <thead>
                <tr>
                  <th scope="col">{{ 'backoffice.licences.table.component' | translate }}</th>
                  <th scope="col" class="text-end">{{ 'backoffice.licences.table.purchased' | translate }}</th>
                  <th scope="col" class="text-end">{{ 'backoffice.licences.table.assigned' | translate }}</th>
                  <th scope="col" class="text-end">{{ 'backoffice.licences.table.free' | translate }}</th>
                  <th scope="col" class="text-end">{{ 'backoffice.licences.table.cancellable' | translate }}</th>
                  <th scope="col" class="text-end">{{ 'backoffice.licences.table.pending_end' | translate }}</th>
                  <th scope="col" class="text-end">{{ 'backoffice.licences.table.half_state' | translate }}</th>
                  <th scope="col" class="text-end">{{ 'backoffice.licences.table.stripe_quantity' | translate }}</th>
                  <th scope="col">{{ 'backoffice.licences.table.in_sync' | translate }}</th>
                </tr>
              </thead>

              <tbody>
                @for (component of facade.components(); track component.component_type) {
                  <tr>
                    <td class="cell-strong">
                      {{ component.componentLabelKey | translate }} — {{ component.stripeProductNameKey | translate }}
                    </td>
                    <td class="text-end">{{ component.purchased }}</td>
                    <td class="text-end">{{ component.assigned }}</td>
                    <td class="text-end" data-testid="component-free">{{ component.free }}</td>
                    <td class="text-end">{{ component.cancellable }}</td>
                    <td class="text-end">{{ component.pending_cancellation }}</td>
                    <td class="text-end">{{ component.half_state }}</td>
                    <td class="text-end">{{ component.stripe_quantity ?? unresolved }}</td>
                    <td>
                      <av-status-chip
                        [label]="
                          (component.isDrifted
                            ? 'backoffice.licences.chips.drifted'
                            : 'backoffice.licences.chips.in_sync'
                          ) | translate
                        "
                        [tone]="component.isDrifted ? 'danger' : 'positive'"
                      ></av-status-chip>
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>

          @if (facade.hasDrift()) {
            <div class="av-banner p-3 d-flex align-items-start gap-2" data-testid="portfolio-drift">
              <mat-icon aria-hidden="true">sync_problem</mat-icon>
              <span>{{ 'backoffice.licences.notes.drift' | translate }}</span>
            </div>
          }

          <p class="av-meta m-0">{{ 'backoffice.licences.notes.agent_absent' | translate }}</p>

          <div class="d-flex flex-wrap gap-2">
            <button
              type="button"
              class="av-btn av-btn-primary"
              [disabled]="!facade.canAdjust() || !facade.isAdjustable() || facade.isMutating()"
              (click)="openAdjust()"
              data-testid="portfolio-adjust"
            >
              {{ 'backoffice.licences.actions.adjust_quantity' | translate }}
            </button>

            <button
              type="button"
              class="av-btn"
              [disabled]="!facade.canAdjust() || !facade.isAdjustable() || facade.isMutating()"
              (click)="openResumeQuantity()"
              data-testid="portfolio-resume-quantity"
            >
              {{ 'backoffice.licences.actions.resume_renewal' | translate }}
            </button>
          </div>
        </section>

        <section class="av-card mt-3 p-4 d-flex flex-column gap-3" data-testid="portfolio-free-pool">
          <div class="d-flex flex-column gap-1">
            <h3 class="av-section-title m-0">{{ 'backoffice.licences.portfolio.free_pool.title' | translate }}</h3>
            <p class="av-meta m-0">{{ 'backoffice.licences.portfolio.free_pool.hint' | translate }}</p>
          </div>

          @if (facade.freePool(); as pool) {
            <div class="d-flex flex-wrap gap-4">
              @for (component of pool.components; track component.component_type) {
                <div class="d-flex flex-column gap-1" data-testid="free-pool-component">
                  <span class="av-meta">{{ componentLabelKey(component.component_type) | translate }}</span>
                  <span class="cell-strong" data-testid="free-pool-assignable">
                    {{
                      'backoffice.licences.portfolio.free_pool.assignable'
                        | translate: { count: component.assignable }
                    }}
                  </span>
                  <span class="av-meta">
                    {{
                      'backoffice.licences.portfolio.free_pool.breakdown'
                        | translate: { ending: component.ending, half: component.half_state }
                    }}
                  </span>

                  @if (component.items.length === 0) {
                    <span class="av-meta">{{ 'backoffice.licences.portfolio.free_pool.empty' | translate }}</span>
                  }
                </div>
              }
            </div>
          } @else {
            <p class="av-meta m-0">{{ 'backoffice.licences.portfolio.free_pool.loading' | translate }}</p>
          }
        </section>

        <section class="av-card mt-3 p-4 d-flex flex-column gap-3" data-testid="portfolio-roster">
          <div class="d-flex flex-column gap-1">
            <h3 class="av-section-title m-0">{{ 'backoffice.licences.portfolio.roster_title' | translate }}</h3>
            <p class="av-meta m-0">{{ 'backoffice.licences.portfolio.half_state_filter_hint' | translate }}</p>
          </div>

          <div class="filter-row d-flex flex-wrap align-items-end gap-2">
            <label class="d-flex flex-column gap-1" for="licence-component">
              <span class="av-meta">{{ 'backoffice.licences.filters.component' | translate }}</span>
              <select
                id="licence-component"
                class="av-field"
                [value]="facade.filters().component_type"
                (change)="changeFilter('component_type', $any($event.target).value)"
                data-testid="licence-component-filter"
              >
                <option value="">{{ 'backoffice.licences.filters.any_component' | translate }}</option>
                @for (option of componentOptions; track option) {
                  <option [value]="option">{{ componentLabelKey(option) | translate }}</option>
                }
              </select>
            </label>

            <label class="d-flex flex-column gap-1" for="licence-state">
              <span class="av-meta">{{ 'backoffice.licences.filters.state' | translate }}</span>
              <select
                id="licence-state"
                class="av-field"
                [value]="facade.filters().state"
                (change)="changeFilter('state', $any($event.target).value)"
                data-testid="licence-state-filter"
              >
                <option value="">{{ 'backoffice.licences.filters.any_state' | translate }}</option>
                @for (option of stateOptions; track option) {
                  <option [value]="option">{{ stateLabelKeys[option] | translate }}</option>
                }
              </select>
            </label>

            <label class="d-flex flex-column gap-1" for="licence-holder">
              <span class="av-meta">{{ 'backoffice.licences.filters.holder' | translate }}</span>
              <select
                id="licence-holder"
                class="av-field"
                [value]="facade.filters().holder"
                (change)="changeFilter('holder', $any($event.target).value)"
                data-testid="licence-holder-filter"
              >
                <option value="">{{ 'backoffice.licences.filters.any_holder' | translate }}</option>
                @for (option of holderOptions; track option) {
                  <option [value]="option">{{ holderLabelKeys[option] | translate }}</option>
                }
              </select>
            </label>

            @if (facade.hasActiveFilters()) {
              <button
                type="button"
                class="av-btn"
                (click)="clearFilters()"
                data-testid="licence-clear-filters"
              >
                {{ 'backoffice.licences.filters.clear' | translate }}
              </button>
            }
          </div>

          @if (facade.halfStateTotal() > 0) {
            <div class="av-banner p-3 d-flex align-items-start gap-2" data-testid="half-state-banner">
              <mat-icon aria-hidden="true">rule</mat-icon>
              <div class="d-flex flex-column gap-2">
                <span>
                  {{
                    'backoffice.licences.portfolio.half_state_count'
                      | translate: { count: facade.halfStateTotal() }
                  }}
                  {{ 'backoffice.licences.notes.half_state' | translate }}
                  {{ 'backoffice.licences.notes.half_state_repair' | translate }}
                </span>

                <button
                  type="button"
                  class="av-btn align-self-start"
                  (click)="showHalfState()"
                  data-testid="half-state-filter-button"
                >
                  {{ 'backoffice.licences.actions.show_half_state' | translate }}
                </button>
              </div>
            </div>
          }

          @if (isEmpty()) {
            <av-empty-state
              icon="confirmation_number"
              [title]="'backoffice.licences.empty.filtered_title' | translate"
              [message]="'backoffice.licences.empty.filtered_message' | translate"
            ></av-empty-state>
          } @else {
            <div class="table-scroll">
              <table class="av-table w-100" data-testid="licence-table">
                <thead>
                  <tr>
                    <th scope="col">{{ 'backoffice.licences.table.component' | translate }}</th>
                    <th scope="col">{{ 'backoffice.licences.table.state' | translate }}</th>
                    <th scope="col">{{ 'backoffice.licences.table.holder' | translate }}</th>
                    <th scope="col">{{ 'backoffice.licences.table.assigned' | translate }}</th>
                    <th scope="col">{{ 'backoffice.licences.table.ends' | translate }}</th>
                    <th scope="col">{{ 'backoffice.licences.actions.title' | translate }}</th>
                  </tr>
                </thead>

                <tbody>
                  @for (licence of facade.licences(); track licence.id) {
                    <tr>
                      <td class="cell-strong">{{ licence.componentLabelKey | translate }}</td>

                      <td>
                        <av-status-chip
                          [label]="licence.stateLabelKey | translate"
                          [tone]="licence.stateTone"
                        ></av-status-chip>
                      </td>

                      <td class="cell-muted">
                        {{ licence.holderLabelKey | translate }} — {{ licence.holderDisplay }}

                        @if (licence.holderIsDeleted) {
                          <span class="av-meta" data-testid="licence-holder-deleted">
                            {{ 'backoffice.licences.table.holder_deleted' | translate }}
                          </span>
                        }
                      </td>

                      <td class="cell-muted">
                        {{ licence.assigned_at ? (licence.assigned_at | date: dateFormat) : unresolved }}
                      </td>

                      <td class="cell-muted">
                        @if (licence.isEnding) {
                          <span [matTooltip]="'backoffice.licences.notes.cancels_at_scheduled' | translate" data-testid="licence-ends-at">
                            {{ licence.cancels_at | date: dateFormat }}
                          </span>
                        } @else {
                          <span>{{ unresolved }}</span>
                        }
                      </td>

                      <td>
                        <div class="d-flex flex-wrap gap-2">
                          @if (licence.isFree) {
                            <button
                              type="button"
                              class="av-btn"
                              [disabled]="!facade.canAssign() || facade.isMutating()"
                              (click)="openAssign(licence)"
                              data-testid="licence-assign"
                            >
                              {{ 'backoffice.licences.actions.assign' | translate }}
                            </button>
                          } @else {
                            <button
                              type="button"
                              class="av-btn"
                              [disabled]="!facade.canAssign() || facade.isMutating()"
                              [matTooltip]="'backoffice.licences.release.root_protected' | translate"
                              (click)="openRelease(licence)"
                              data-testid="licence-release"
                            >
                              {{ 'backoffice.licences.actions.release' | translate }}
                            </button>
                          }

                          @if (licence.is_half_state) {
                            <button
                              type="button"
                              class="av-btn"
                              [disabled]="!facade.canAssign() || facade.isMutating()"
                              [matTooltip]="'backoffice.licences.notes.half_state_repair' | translate"
                              (click)="openRelease(licence)"
                              data-testid="licence-repair-half-state"
                            >
                              {{ 'backoffice.licences.actions.repair_by_release' | translate }}
                            </button>
                          }

                          @if (licence.isResumable) {
                            <button
                              type="button"
                              class="av-btn"
                              [disabled]="!facade.canAdjust() || facade.isMutating()"
                              (click)="openResume(licence)"
                              data-testid="licence-resume"
                            >
                              {{ 'backoffice.licences.actions.resume' | translate }}
                            </button>
                          } @else if (licence.isCancellable) {
                            <button
                              type="button"
                              class="av-btn av-btn-danger"
                              [disabled]="!facade.canAdjust() || facade.isMutating()"
                              (click)="openCancel(licence)"
                              data-testid="licence-cancel"
                            >
                              {{ 'backoffice.licences.actions.cancel_renewal' | translate }}
                            </button>
                          }
                        </div>
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>

            <av-paginator
              [page]="facade.page()"
              [pageSize]="facade.pageSize()"
              [total]="facade.total()"
              (pageChange)="goToPage($event)"
              (pageSizeChange)="changePageSize($event)"
            ></av-paginator>
          }
        </section>
      }
    }
    @default {
      <av-empty-state
        class="mt-3"
        icon="hourglass_empty"
        [title]="'backoffice.licences.portfolio.loading_title' | translate"
        [message]="'backoffice.licences.portfolio.loading_message' | translate"
      ></av-empty-state>
    }
  }
}
